import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar as CalIcon, Clock, Plus, Trash2, Sparkles, AlertTriangle } from "lucide-react";

const WEEKDAYS = [
    { i: 1, l: "Mon" }, { i: 2, l: "Tue" }, { i: 3, l: "Wed" },
    { i: 4, l: "Thu" }, { i: 5, l: "Fri" }, { i: 6, l: "Sat" }, { i: 0, l: "Sun" },
];

function ymdToday() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function ymdAddDays(n) {
    const d = new Date(); d.setDate(d.getDate() + n);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function timeToMin(s) {
    const [h, m] = (s || "0:0").split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
}

export default function BulkSlotsDialog({ open, onClose, onCreated }) {
    const [date_start, setStart] = useState(ymdToday());
    const [date_end, setEnd] = useState(ymdAddDays(7));
    const [weekdays, setWeekdays] = useState([1, 2, 3, 4, 5]);  // Mon-Fri default
    const [blocks, setBlocks] = useState([{ start: "10:00", end: "13:00" }]);
    const [duration, setDuration] = useState(30);
    const [mode, setMode] = useState("online");
    const [saving, setSaving] = useState(false);
    const [conflicts, setConflicts] = useState([]);
    const [checking, setChecking] = useState(false);

    // Check conflicts whenever inputs change (debounced)
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(async () => {
            setChecking(true);
            try {
                const { data } = await api.post("/calendar/interviews/check-conflicts", {
                    date_start, date_end, weekdays,
                    time_blocks: blocks, duration_minutes: Number(duration),
                });
                setConflicts(data?.conflicts || []);
            } catch { setConflicts([]); }
            finally { setChecking(false); }
        }, 350);
        return () => clearTimeout(t);
    }, [open, date_start, date_end, weekdays, blocks, duration]);

    if (!open) return null;

    const previewCount = (() => {
        try {
            const d0 = new Date(date_start);
            const d1 = new Date(date_end);
            if (isNaN(d0) || isNaN(d1) || d0 > d1) return 0;
            let n = 0;
            for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
                const py = d.getDay();   // 0=Sun..6=Sat — matches our weekday enum directly
                if (!weekdays.length || weekdays.includes(py)) {
                    for (const b of blocks) {
                        const span = timeToMin(b.end) - timeToMin(b.start);
                        if (span > 0 && duration > 0) n += Math.floor(span / duration);
                    }
                }
            }
            return n;
        } catch { return 0; }
    })();

    const toggleDay = (i) => {
        setWeekdays(weekdays.includes(i) ? weekdays.filter((x) => x !== i) : [...weekdays, i]);
    };
    const addBlock = () => setBlocks([...blocks, { start: "14:00", end: "17:00" }]);
    const updateBlock = (idx, k, v) => {
        const arr = [...blocks]; arr[idx] = { ...arr[idx], [k]: v }; setBlocks(arr);
    };
    const removeBlock = (idx) => setBlocks(blocks.filter((_, i) => i !== idx));

    const submit = async () => {
        if (previewCount === 0) { toast.error("No slots would be created — adjust your inputs"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/calendar/interviews/bulk", {
                date_start, date_end, weekdays, time_blocks: blocks,
                duration_minutes: Number(duration), online_offline: mode, mode: mode === "online" ? "Video" : "Onsite",
                round_name: "Interview slot",
            });
            toast.success(`Created ${data.created} interview slots`);
            onCreated?.();
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-2xl rounded-none border border-border" data-testid="bulk-slots-dialog">
                <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Bulk-create interview slots</DialogTitle>
                <DialogDescription>
                    Block off recurring availability — recruiters can assign candidates & jobs later.
                </DialogDescription>
                <div className="space-y-4 mt-3">
                    {/* Date range */}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-xs">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">From</span>
                            <Input type="date" value={date_start} onChange={(e) => setStart(e.target.value)} className="rounded-none mt-1" data-testid="bulk-start" />
                        </label>
                        <label className="block text-xs">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">To</span>
                            <Input type="date" value={date_end} onChange={(e) => setEnd(e.target.value)} className="rounded-none mt-1" data-testid="bulk-end" />
                        </label>
                    </div>

                    {/* Weekdays */}
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1">On these days</div>
                        <div className="flex gap-1.5 flex-wrap">
                            {WEEKDAYS.map((d) => (
                                <button
                                    key={d.i}
                                    type="button"
                                    onClick={() => toggleDay(d.i)}
                                    className={`px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest border ${weekdays.includes(d.i) ? "bg-ikb text-white border-ikb" : "bg-white border-border hover:bg-secondary"}`}
                                    data-testid={`bulk-day-${d.l.toLowerCase()}`}
                                >{d.l}</button>
                            ))}
                        </div>
                    </div>

                    {/* Time blocks */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Time blocks</span>
                            <Button type="button" onClick={addBlock} variant="outline" className="rounded-none h-7 text-xs px-2" data-testid="bulk-add-block"><Plus className="w-3 h-3 mr-1" /> Add block</Button>
                        </div>
                        <div className="space-y-1.5">
                            {blocks.map((b, i) => (
                                <div key={i} className="flex items-center gap-2" data-testid={`bulk-block-${i}`}>
                                    <Clock className="w-3.5 h-3.5 text-foreground/50" />
                                    <Input type="time" value={b.start} onChange={(e) => updateBlock(i, "start", e.target.value)} className="rounded-none h-9 flex-1" />
                                    <span className="text-xs text-foreground/60">to</span>
                                    <Input type="time" value={b.end} onChange={(e) => updateBlock(i, "end", e.target.value)} className="rounded-none h-9 flex-1" />
                                    <Button type="button" onClick={() => removeBlock(i)} variant="outline" className="rounded-none h-9 px-2 text-rose-700 border-rose-300" disabled={blocks.length === 1}><Trash2 className="w-3.5 h-3.5" /></Button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Duration + mode */}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-xs">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Slot length (min)</span>
                            <Input type="number" min={5} step={5} value={duration} onChange={(e) => setDuration(e.target.value)} className="rounded-none mt-1" data-testid="bulk-duration" />
                        </label>
                        <div className="block text-xs">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Mode</span>
                            <div className="inline-flex border border-border bg-white mt-1 w-full">
                                <button type="button" onClick={() => setMode("online")} className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest ${mode === "online" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="bulk-mode-online">Online</button>
                                <button type="button" onClick={() => setMode("offline")} className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest border-l border-border ${mode === "offline" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="bulk-mode-offline">Offline</button>
                            </div>
                        </div>
                    </div>

                    {/* Conflict warning */}
                    {conflicts.length > 0 && (
                        <div className="border border-amber-400 bg-amber-50 px-4 py-3 space-y-1" data-testid="bulk-conflicts">
                            <div className="text-xs font-mono uppercase tracking-widest text-amber-800 inline-flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5" /> {conflicts.length} potential conflict{conflicts.length === 1 ? "" : "s"} with your existing schedule
                            </div>
                            <ul className="text-[11px] font-mono text-amber-900 space-y-0.5 max-h-32 overflow-y-auto">
                                {conflicts.slice(0, 6).map((c, i) => (
                                    <li key={i}>
                                        • {new Date(c.slot_start).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} clashes with an interview {c.owner ? `by ${c.owner}` : ""} ({c.reason})
                                    </li>
                                ))}
                                {conflicts.length > 6 && <li>• +{conflicts.length - 6} more…</li>}
                            </ul>
                        </div>
                    )}

                    {/* Preview */}
                    <div className="border border-border bg-secondary/40 px-4 py-3 flex items-center justify-between" data-testid="bulk-preview">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Will create</div>
                            <div className="font-display font-black text-3xl text-ikb mt-0.5">{previewCount} <span className="text-base font-normal text-foreground/60">slots</span></div>
                        </div>
                        <CalIcon className="w-8 h-8 text-ikb/40" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="bulk-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={saving || previewCount === 0} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="bulk-create">
                        <Sparkles className="w-4 h-4 mr-1" /> {saving ? "Creating…" : `Create ${previewCount} slots`}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
